import { Grid, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import NavBar from '../components/NavBar';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PageType, setCurrentPage } from '../../store/slices/general.slice';
import { primaryColor } from '../../assets/styles/styles';

export default function CompetitionTerms({}: any) {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    dispatch(setCurrentPage(PageType.CONTACT));
  }, []);

  return (
    <>
      <Helmet>
        <meta
          name="title"
          content="RouteBuddies | Competition Terms & Conditions"
        />
        <meta
          name="description"
          content="Terms and Conditions for the RouteBuddies South Wales Pilots Summer 2025 competition."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <NavBar theme={theme} navigate={navigate} loggedIn={loggedIn} />

      {/* Section 1 */}
      <Grid container xs={12} justifyContent={'center'}>
        <Grid
          item
          container
          xs={10}
          justifyContent={'center'}
          style={{ marginTop: 0 }}
        >
          {/* Title */}
          <Grid
            item
            container
            xs={12}
            justifyContent={'flex-start'}
            flexDirection={'row'}
            style={{ borderBottom: '0.5px solid grey', marginBottom: 15 }}
          >
            <Typography
              variant="h2"
              fontWeight={'bold'}
              fontFamily={'Urbanist'}
              style={{
                fontFamily: 'UrbanistBold',
                fontWeight: '900',
                marginTop: 40,
                marginBottom: 25,
                color: primaryColor,
              }}
            >
              Competition Terms & Conditions
            </Typography>
          </Grid>

          {/* Content */}
          <Grid
            container
            item
            xs={12}
            md={10}
            lg={7}
            alignItems={'flex-start'}
            style={{ padding: '15px' }}
            flexDirection={'column'}
          >
            <Typography
              variant={'body2'}
              style={{
                fontFamily: 'Urbanist',
                fontSize: 16,
                letterSpacing: 1.3,
                color: 'rgba(40,40,40)',
                marginBottom: 20,
              }}
            >
              These Terms & Conditions apply to the{' '}
              <strong>RouteBuddies South Wales Pilots Summer 2025</strong>{' '}
              competition. By entering, you agree to the following:
            </Typography>

            {/* Section: Eligibility */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              1. Eligibility
            </Typography>
            <ul>
              <li>
                The competition is open exclusively to verified users of the{' '}
                <strong>RouteBuddies South Wales Pilots Summer 2025</strong>{' '}
                programme.
              </li>
              <li>Participants must be 18 years or older.</li>
              <li>
                Employees, contractors, or affiliates of RouteBuddies are not
                eligible to win.
              </li>
              <li>
                <strong>No purchase is necessary</strong> to enter or
                participate.
              </li>
            </ul>

            {/* Section: Points */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              2. How to Earn Points
            </Typography>
            <ul>
              <li>Creating posts</li>
              <li>Creating or joining activities/events</li>
              <li>Adding routes</li>
              <li>Commenting on posts</li>
              <li>Liking posts</li>
            </ul>
            <Typography
              variant="body2"
              style={{ fontFamily: 'Urbanist', marginTop: 10 }}
            >
              Points are awarded only for{' '}
              <strong>genuine, original, and meaningful</strong> activity.
            </Typography>

            {/* Section: Fair Use */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              3. Fair Use & Anti-Spam Rules
            </Typography>
            <ul>
              <li>
                Duplicate, low-effort, or meaningless contributions (e.g., spam,
                repeated text/emoji, or empty routes) will not earn points.
              </li>
              <li>
                Excessive posting, commenting, or liking in a short period of
                time may result in removal of points or disqualification.
              </li>
              <li>
                Automated activity, fake accounts, or coordinated
                “like-swapping” is strictly prohibited.
              </li>
              <li>
                RouteBuddies reserves the right to review content and adjust or
                remove points where cheating or manipulation is suspected.
              </li>
            </ul>

            {/* Section: Cheating */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              4. Cheating & Disqualification
            </Typography>
            <ul>
              <li>
                Any attempt to exploit the system (including but not limited to
                multiple accounts, bots, spam, or fabricated content) will
                result in immediate disqualification.
              </li>
              <li>
                RouteBuddies may suspend or ban accounts found in violation.
              </li>
            </ul>

            {/* Section: Prize */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              5. Prize Allocation & Delivery
            </Typography>
            <ul>
              <li>
                Winners will be determined by the highest number of{' '}
                <strong>valid points</strong> earned during the competition
                period.
              </li>
              <li>
                In the event of a tie, RouteBuddies may use additional criteria
                (such as quality and community value of contributions) to
                decide.
              </li>
              <li>
                Winners will be notified via the email address linked to their
                RouteBuddies account within 14 days of competition close.
              </li>
              <li>
                RouteBuddies reserves the right to verify the identity and
                eligibility of winners before awarding prizes.
              </li>
              <li>
                Prizes are non-transferable and no cash alternative is available
                unless explicitly stated.
              </li>
            </ul>

            {/* Section: Period */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              6. Competition Period
            </Typography>
            <ul>
              <li>
                The competition begins on <strong>[Start Date]</strong> and ends
                on <strong>29th October 2025</strong>.
              </li>
              <li>Points earned outside this period will not count.</li>
            </ul>

            {/* Section: Privacy */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              7. Privacy & Data Use
            </Typography>
            <ul>
              <li>
                By participating, users agree that RouteBuddies may collect and
                process the data necessary to operate the competition (such as
                activity logs, posts, and engagement).
              </li>
              <li>
                Personal data will be handled in accordance with the
                RouteBuddies Privacy Policy and will not be shared with third
                parties without consent.
              </li>
            </ul>

            {/* Section: Liability */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              8. Liability Disclaimer
            </Typography>
            <ul>
              <li>
                RouteBuddies accepts no responsibility for technical failures,
                interruptions, or errors outside of its reasonable control.
              </li>
              <li>
                RouteBuddies is not liable for any loss, damage, or injury
                arising from participation in the competition or use of prizes.
              </li>
            </ul>

            {/* Section: General */}
            <Typography
              variant="h5"
              style={{
                fontFamily: 'UrbanistBold',
                marginTop: 25,
                marginBottom: 10,
                color: primaryColor,
              }}
            >
              9. General
            </Typography>
            <ul>
              <li>
                By entering, participants agree to abide by these Terms &
                Conditions and the RouteBuddies Community Guidelines.
              </li>
              <li>
                RouteBuddies reserves the right to amend, suspend, or terminate
                the competition at any time if necessary.
              </li>
              <li>All decisions made by RouteBuddies are final.</li>
            </ul>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
